/**
 * Create an online account (tracker item 45, phase 3): /portal/register and
 * /portal/:slug/register. Email, name, phone, and ONE of two ways to be found:
 *   - the registration code printed on any invoice (strong proof by itself), or
 *   - any TWO of: a street address of a service property, the last 4 digits of
 *     the phone number on file, a recent invoice number or amount.
 * There is NO password here: the customer chooses it when they confirm their
 * email, so whoever registers an address can never set its password. The
 * result is the same neutral "check your email" whatever exists or matches.
 */
import React, { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { createAPIClient, IPortalApi } from '../../api';
import { PortalPublicLayout, portalLinkClass } from './PortalPublicLayout';
import { InlineAlert } from './PortalStates';
import { inputClass, labelClass, primaryButtonClass } from '../../components/formStyles';
import {
  describePortalFailure,
  proofFieldCount,
  PROOF_FIELD_COUNT_REQUIRED,
  RegistrationForm,
  registrationProblems,
  registrationRequest,
  slugFromParam,
} from '../../utils/portal';

const EMPTY: RegistrationForm = {
  tenantSlug: '', email: '', name: '', phone: '', invoiceCode: '', address: '', phoneLast4: '', invoiceNumber: '', invoiceAmount: '',
};

function Field({ id, label, hint, error, children }: { id: string; label: string; hint?: string; error?: string; children: React.ReactNode }) {
  return (
    <div>
      <label htmlFor={id} className={labelClass}>{label}</label>
      {children}
      {hint && <p id={`${id}-hint`} className="mt-1 text-xs text-gray-500">{hint}</p>}
      {error && <p id={`${id}-error`} className="mt-1 text-xs text-red-700">{error}</p>}
    </div>
  );
}

export function PortalRegisterPage() {
  const api = useMemo(() => createAPIClient() as IPortalApi, []);
  const { slug: slugParam } = useParams<{ slug: string }>();
  const [search] = useSearchParams();
  const fixedSlug = slugFromParam(slugParam) || slugFromParam(search.get('company') ?? undefined);
  const [form, setForm] = useState<RegistrationForm>({ ...EMPTY, tenantSlug: fixedSlug });
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);

  const set = (key: keyof RegistrationForm) => (event: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [key]: event.target.value }));
  const problems = registrationProblems(form, !fixedSlug);
  const hasCode = form.invoiceCode.trim().length > 0;
  const proofCount = proofFieldCount(form);
  const loginHref = fixedSlug ? `/portal/${fixedSlug}` : '/portal/login';

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (busy) return;
    setTouched(true);
    setError(null);
    if (Object.keys(problems).length > 0) return;
    setBusy(true);
    try {
      const result = await api.registerPortalAccount(registrationRequest(form, fixedSlug || form.tenantSlug));
      setSent(result.message);
    } catch (cause) {
      setError(describePortalFailure(cause, 'We could not send your registration. Please check the form and try again.').message);
    } finally {
      setBusy(false);
    }
  }

  if (sent !== null) {
    return (
      <PortalPublicLayout title="Check your email">
        <div role="status" className="space-y-3 text-sm text-gray-700" data-testid="register-result">
          <p>If this email address can be registered, we have sent a message to it with a link to confirm it and choose your password.</p>
          <p>The link works once and expires in 24 hours. If nothing arrives, check your spam folder.</p>
          <p className="text-xs text-gray-500">For your security this message is the same whether or not we could match your details.</p>
        </div>
        <div className="mt-4 flex flex-col">
          <Link to={loginHref} className={portalLinkClass}>Already confirmed? Sign in</Link>
        </div>
      </PortalPublicLayout>
    );
  }

  const shown = (key: keyof RegistrationForm) => (touched ? problems[key] : undefined);
  const input = inputClass + ' min-h-[44px] text-base';

  return (
    <PortalPublicLayout
      title="Create your online account"
      intro="See your invoices and visits, and pay online. We will email you a link to confirm your address and choose a password."
    >
      <form onSubmit={(e) => void submit(e)} className="space-y-4" noValidate>
        {!fixedSlug && (
          <Field id="reg-company" label="Company" hint="The company name from your invoice link, such as company-a." error={shown('tenantSlug')}>
            <input id="reg-company" className={input} value={form.tenantSlug} onChange={set('tenantSlug')} disabled={busy} autoCapitalize="none" autoCorrect="off" required aria-describedby="reg-company-hint" />
          </Field>
        )}
        <Field id="reg-name" label="Your name" error={shown('name')}>
          <input id="reg-name" className={input} value={form.name} onChange={set('name')} disabled={busy} autoComplete="name" required />
        </Field>
        <Field id="reg-email" label="Email address" hint="We send the confirmation link here." error={shown('email')}>
          <input id="reg-email" type="email" className={input} value={form.email} onChange={set('email')} disabled={busy} autoComplete="email" required aria-describedby="reg-email-hint" />
        </Field>
        <Field id="reg-phone" label="Phone number" error={shown('phone')}>
          <input id="reg-phone" type="tel" className={input} value={form.phone} onChange={set('phone')} disabled={busy} autoComplete="tel" required />
        </Field>

        <fieldset className="space-y-3 rounded-lg border border-gray-200 p-3">
          <legend className="px-1 text-sm font-semibold text-gray-900">Find your account</legend>
          <Field
            id="reg-code" label="Invoice code (optional)" error={shown('invoiceCode')}
            hint="Found on your invoice, and in the invoice email next to 'Create your online account'. It looks like K7M2-X9QP. With a code you can skip the questions below."
          >
            <input id="reg-code" className={input + ' uppercase'} value={form.invoiceCode} onChange={set('invoiceCode')} disabled={busy} autoCapitalize="characters" autoCorrect="off" maxLength={12} aria-describedby="reg-code-hint" />
          </Field>
          <div className={hasCode ? 'opacity-60' : undefined}>
            <p className="mb-2 text-sm text-gray-700" id="proof-explainer">
              No code? Fill in <strong>any two</strong> of the three details below. They help us match you to your account and are never shown back.
              {hasCode && ' (Not needed when you give a code.)'}
            </p>
            <div className="space-y-3" aria-describedby="proof-explainer">
              <Field id="reg-address" label="Street address of a property we serve (optional)" hint="Number and street, for example 12 Oak St." error={shown('address')}>
                <input id="reg-address" className={input} value={form.address} onChange={set('address')} disabled={busy} autoComplete="off" aria-describedby="reg-address-hint" />
              </Field>
              <Field id="reg-last4" label="Last 4 digits of your phone number on file (optional)" error={shown('phoneLast4')}>
                <input id="reg-last4" inputMode="numeric" className={input} value={form.phoneLast4} onChange={set('phoneLast4')} disabled={busy} maxLength={14} autoComplete="off" />
              </Field>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field id="reg-invoice" label="A recent invoice number (optional)" error={shown('invoiceNumber')}>
                  <input id="reg-invoice" className={input} value={form.invoiceNumber} onChange={set('invoiceNumber')} disabled={busy} autoComplete="off" />
                </Field>
                <Field id="reg-amount" label="or the amount of your last invoice (optional)" error={shown('invoiceAmount')}>
                  <input id="reg-amount" inputMode="decimal" className={input} value={form.invoiceAmount} onChange={set('invoiceAmount')} disabled={busy} autoComplete="off" />
                </Field>
              </div>
            </div>
            {!hasCode && proofCount < PROOF_FIELD_COUNT_REQUIRED && (
              <p className="mt-2 text-xs text-gray-500" data-testid="proof-count">
                {proofCount} of {PROOF_FIELD_COUNT_REQUIRED} details given. You can still register: our office will then match your account by hand.
              </p>
            )}
          </div>
        </fieldset>

        {error && <InlineAlert>{error}</InlineAlert>}
        <button type="submit" className={`${primaryButtonClass} w-full text-base`} disabled={busy}>{busy ? 'Sending…' : 'Create account'}</button>
      </form>
      <div className="mt-4 flex flex-col">
        <Link to={loginHref} className={portalLinkClass}>Already have an account? Sign in</Link>
      </div>
    </PortalPublicLayout>
  );
}
